import { Box, Container, Typography, Paper, Divider } from "@mui/material";

export function PrivacyPolicy() {
  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", py: 6 }}>
      <Container maxWidth="md">
        <Paper elevation={0} variant="outlined" sx={{ p: { xs: 3, md: 6 } }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Last updated: May 15, 2026
          </Typography>
          <Divider sx={{ mb: 4 }} />

          <Section title="1. Information We Collect">
            <strong>Account information:</strong> When you sign up, we collect
            your name, email address, and role (contractor or client).
            Contractors may also provide a business name and phone number.
            <br /><br />
            <strong>Project data:</strong> We store project details, milestones,
            plans, progress updates, messages, and documents you create or
            upload within the platform.
            <br /><br />
            <strong>Payment information:</strong> Contractor subscription
            payments are processed by Stripe. BuildBridge does not store your
            full credit card number. Stripe shares billing status and
            subscription metadata with us.
            <br /><br />
            <strong>Usage data:</strong> We may collect logs of actions taken
            within the platform (e.g., logins, page visits) for security and
            debugging purposes.
          </Section>

          <Section title="2. How We Use Your Information">
            We use your information to: (a) provide and improve the BuildBridge
            platform; (b) send transactional emails such as invitations,
            notifications, and account confirmations; (c) process subscription
            payments; (d) investigate security incidents and enforce our Terms
            of Service.
            <br /><br />
            We do not sell your personal information to third parties.
          </Section>

          <Section title="3. Information Sharing">
            We share your information only with:
            <br /><br />
            <strong>Supabase:</strong> Our database and authentication provider,
            which stores all platform data on our behalf.
            <br /><br />
            <strong>Stripe:</strong> Our payment processor, for subscription
            billing.
            <br /><br />
            <strong>SendGrid:</strong> Our email provider, for sending
            transactional emails.
            <br /><br />
            We may also disclose information if required by law or to protect
            the rights and safety of our users.
          </Section>

          <Section title="4. Data Retention">
            We retain your account and project data for as long as your account
            is active. If you delete your account, your personal profile is
            removed. Note that contractors retain a read-only record of your
            name and email on projects you were associated with, for legitimate
            business record-keeping purposes (e.g., warranty and payment
            history).
          </Section>

          <Section title="5. Your Rights">
            Depending on your location, you may have the right to: access,
            correct, or delete your personal data; object to or restrict certain
            processing; and data portability. To exercise these rights, contact
            us at <strong>privacy@buildbridge.app</strong>. We will respond
            within 30 days.
            <br /><br />
            California residents may have additional rights under the CCPA,
            including the right to know what personal information we have
            collected about you and to request deletion.
          </Section>

          <Section title="6. Cookies">
            BuildBridge uses only essential cookies required for authentication
            and session management. We do not use advertising or tracking
            cookies.
          </Section>

          <Section title="7. Security">
            We implement industry-standard security measures including encrypted
            data transmission (TLS), row-level security on all database tables,
            and access controls limiting which users can view which data. No
            system is completely secure; we cannot guarantee absolute security
            of your data.
          </Section>

          <Section title="8. Children">
            BuildBridge is not intended for users under 18 years of age. We do
            not knowingly collect personal information from minors.
          </Section>

          <Section title="9. Changes to This Policy">
            We may update this Privacy Policy from time to time. We will notify
            you of material changes by email or by a notice within the platform.
            Continued use of BuildBridge after changes take effect constitutes
            acceptance.
          </Section>

          <Section title="10. Contact">
            Questions or concerns about your privacy? Contact us at{" "}
            <strong>privacy@buildbridge.app</strong>.
          </Section>
        </Paper>
      </Container>
    </Box>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
        {children}
      </Typography>
    </Box>
  );
}
